import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import axiosClient from '@/api/axios-client';
import Modal from '@/components/feedBack/Modal';
import Button from '@/components/general/Button';
import { useStateContext } from '@/contexts/ContextProvider';
import useCrossTabFacilityChange from '@/hooks/useCrossTabFacilityChange';
import type { Facility } from '@/pages/settings/facilities/useFacility';

/**
 * Bloque l'onglet dont l'établissement a été changé dans un autre onglet.
 *
 * Pas de rechargement automatique : l'onglet peut porter un formulaire non
 * enregistré (cas de l'incident : un widget newsletter en cours d'édition). La
 * modale, non fermable, empêche de continuer à écrire dans le mauvais
 * établissement ; revenir sur le bon établissement dans l'autre onglet la ferme
 * et rend la main sans rien perdre.
 */
export default function CrossTabFacilityChangeModal() {
    const { t } = useTranslation('navigation');
    const { currentFacility, user } = useStateContext();
    const change = useCrossTabFacilityChange(currentFacility?.id, user?.facility_id);
    const { pathname } = useLocation();

    const { data: targetFacility } = useQuery(
        ['cross-tab-facility', change?.facilityId],
        async () => (await axiosClient.get<Facility>(`/facilities/${change?.facilityId}`)).data,
        { enabled: Boolean(change), staleTime: Infinity, retry: false },
    );

    // Le CRM ne dépend pas de l'établissement sélectionné (routes hors tenant) :
    // ouvrir un flow depuis la fiche bascule l'autre onglet sans bloquer celui-ci.
    // Le changement reste mémorisé : quitter le CRM fait réapparaître la modale.
    if (!change || pathname.startsWith('/super-admin/crm')) return null;

    const targetName = targetFacility?.name;
    const currentName = currentFacility?.name ?? '';

    return (
        <Modal
            open={true}
            setOpen={() => {
                /* no-op : rester ici écrirait dans l'autre établissement */
            }}
            title={t('cross_tab_facility.title')}
            size="md"
            buttons={
                <Button
                    type="primary"
                    elementType="button"
                    attributes={{ onClick: () => window.location.reload() }}
                >
                    {t('cross_tab_facility.reload')}
                </Button>
            }
        >
            <div className="flex flex-col gap-3 text-sm text-gray-600">
                <p>
                    {targetName
                        ? t('cross_tab_facility.body_named', { name: targetName })
                        : t('cross_tab_facility.body')}
                </p>
                <p>{t('cross_tab_facility.keep_work', { name: currentName })}</p>
            </div>
        </Modal>
    );
}
